import {
	Automation,
	DefineAutomationRequest,
	ProcessBuilderPage,
	Template,
	Variable,
} from '@/scripts/types/generated';
import {Page} from '@inertiajs/core';
import {useTemplates} from '@/scripts/composables/use-templates.ts';

const automations = ref<Automation[]>();
const templates = ref<Template[]>();
const isLoading = ref<boolean>(false);

export const useAutomations = () => {
	const {getTemplateVariables} = useTemplates();

	const refreshAutomations = (page: Page<ProcessBuilderPage>) => {
		automations.value = page.props.automations;
		templates.value = page.props.templates;
	};

	const initAutomations = async () => {
		while (isLoading.value) {
			await new Promise((resolve) => setTimeout(resolve, 100));
		}

		if (automations.value) return;

		isLoading.value = true;
		const page = await sendReloadRequest<ProcessBuilderPage>([
			'automations',
			'templates',
		]);
		refreshAutomations(page);
		isLoading.value = false;
	};

	const createAutomation = async (
		request: DefineAutomationRequest
	): Promise<Automation> => {
		// TODO have a look into unhandled error some day
		const page = await sendCreateRequest<ProcessBuilderPage>(
			route('tenant.automations.store'),
			request,
			['automations', 'templates']
		);

		return new Promise<Automation>((resolve) => {
			refreshAutomations(page);
			const newAutomation = automations.value.find(
				(automation) => automation.name === request.name
			);
			resolve(newAutomation);
		});
	};

	const deleteAutomation = async (slug: string): Promise<void> => {
		const page = await sendDeleteRequest<ProcessBuilderPage>(
			route('tenant.automations.destroy', {
				automation: slug,
			}),
			['automations', 'templates']
		);

		return new Promise<void>((resolve) => {
			refreshAutomations(page);
			resolve();
		});
	};

	const getAutomationTemplate = async (
		automation: Automation
	): Promise<Template> => {
		if (!templates.value) {
			await initAutomations();
		}

		return templates.value.find(
			(template) =>
				template.versionUuid ===
				automation.nodes.find((node) => node.type === 'action')?.config[
					'template'
				]
		);
	};

	const getAutomationVariables = async (
		automation: Automation
	): Promise<Variable[]> => {
		const variables: Variable[] = [];

		const template = await getAutomationTemplate(automation);
		variables.push(...(await getTemplateVariables(template)));

		return variables;
	};

	const setAutomations = (newAutomations: Automation[]) => {
		automations.value = newAutomations;
	};

	return {
		automations,
		templates,
		initAutomations,
		createAutomation,
		deleteAutomation,
		getAutomationVariables,
		getAutomationTemplate,
		setAutomations,
	};
};
